import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import "./EventCodesTable.css";

const EventCodesTable = ({ codes, onDeleteCode, onUpdateNote }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [saving, setSaving] = useState(false);

  const handleDeleteClick = (code) => {
    setSelectedCode(code);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    onDeleteCode(selectedCode);
    setShowDeleteModal(false);
    setSelectedCode(null);
  };

  const handleCopyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setShowCopyFeedback(true);
    setTimeout(() => {
      setShowCopyFeedback(false);
      setCopiedCode("");
    }, 2000);
  };

  const handleUserIdClick = (userId) => {
    if (userId) {
      navigate(`/users/profile/${userId}`);
    }
  };

  const handleEventIdClick = (eventId) => {
    if (eventId) {
      navigate(`/events/event/${eventId}`);
    }
  };

  const handleEditNote = (code, currentNote) => {
    setEditingNote(code);
    setNoteValue(currentNote || "");
  };

  const handleSaveNote = async (code) => {
    setSaving(true);
    try {
      await onUpdateNote(code, noteValue);
      setEditingNote(null);
      setNoteValue("");
    } catch (error) {
      console.error("Failed to update note:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setNoteValue("");
  };

  return (
    <>
      <div className="table-responsive">
        <Table striped hover className="mb-0">
          <thead>
            <tr>
              <th>Code</th>
              <th>User ID</th>
              <th>Event ID</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.code}>
                <td 
                  className="clickable-cell"
                  onClick={() => handleCopyToClipboard(code.code)}
                  title="Click to copy"
                >
                  {code.code}
                </td>
                <td 
                  className={code.user_id ? "clickable-cell" : ""}
                  onClick={() => handleUserIdClick(code.user_id)}
                  title={code.user_id ? "Click to view user" : ""}
                >
                  {code.user_id || "Not assigned"}
                </td>
                <td 
                  className={code.event_id ? "clickable-cell" : ""}
                  onClick={() => handleEventIdClick(code.event_id)}
                  title={code.event_id ? "Click to view event" : ""}
                >
                  {code.event_id || "Not assigned"}
                </td>
                <td>
                  {editingNote === code.code ? (
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="text"
                        size="sm"
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        placeholder="Enter note..."
                        className="me-2"
                        style={{ minWidth: "150px" }}
                      />
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleSaveNote(code.code)}
                        disabled={saving}
                        className="me-1"
                      >
                        {saving ? "..." : "✓"}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        ✕
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="note-display"
                      onClick={() => handleEditNote(code.code, code.note)}
                      title="Click to edit note"
                      style={{ cursor: "pointer", minHeight: "20px" }}
                    >
                      {code.note || (
                        <span className="text-muted">Click to add note</span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-outline-primary btn-sm me-2"
                    onClick={() => handleCopyToClipboard(code.code)}
                  >
                    Copy
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleDeleteClick(code.code)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {showCopyFeedback && (
        <div className="copy-feedback">
          Copied {copiedCode} to clipboard!
        </div>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the code <strong>{selectedCode}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleConfirmDelete}
          >
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

EventCodesTable.propTypes = {
  codes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      user_id: PropTypes.number,
      event_id: PropTypes.number,
      note: PropTypes.string,
    })
  ).isRequired,
  onDeleteCode: PropTypes.func.isRequired,
  onUpdateNote: PropTypes.func.isRequired,
};

export default EventCodesTable; 